<script setup>
    import { computed, nextTick, unref } from 'vue'
    import { util } from '@/Helpers'
    import { IconDocumentEdit16Regular, IconDocumentError16Filled } from '@iconify-prerendered/vue-fluent'
    import { ArrowRightIcon } from '@heroicons/vue/20/solid'

    const props = defineProps({
        show: {
            type: Boolean,
            default: false,
        },
        documentsModuleRef: {
            type: Object,
            default: null,
        },
    })

    const documentsModule = computed(() => unref(props.documentsModuleRef))

    function getDocumentsModuleValue(key, fallback) {
        if (!documentsModule.value) {
            return fallback
        }

        const resolvedValue = unref(documentsModule.value[key])
        return resolvedValue ?? fallback
    }

    const missingDocumentsAlerts = computed(() => {
        const missingDocuments = getDocumentsModuleValue('missingDocuments', [])
        return Array.isArray(missingDocuments) ? missingDocuments : []
    })

    const pendingDocumentsCountAlert = computed(() => {
        const pendingDocumentsCount = Number(getDocumentsModuleValue('pendingDocumentsCount', 0))
        return Number.isFinite(pendingDocumentsCount) ? pendingDocumentsCount : 0
    })

    const pendingSignatureBundlesAlert = computed(() => {
        const pendingSignatureBundles = getDocumentsModuleValue('pendingSignatureBundles', [])
        return Array.isArray(pendingSignatureBundles) ? pendingSignatureBundles : []
    })

    const missingDocumentsAlertNames = computed(() => {
        return missingDocumentsAlerts.value.map(document => document.name).join(', ')
    })

    const pendingDocumentsRemainingText = computed(() => {
        const now = new Date()
        const expiryDates = pendingSignatureBundlesAlert.value
            .map(bundle => bundle?.signature_pandadoc_expires_at)
            .filter(Boolean)
            .map(expiryDate => new Date(expiryDate))
            .filter(expiryDate => !Number.isNaN(expiryDate.getTime()) && expiryDate.getTime() >= now.getTime())

        if (expiryDates.length === 0) {
            return ''
        }

        const nearestExpiryDate = expiryDates.sort((firstDate, secondDate) => firstDate.getTime() - secondDate.getTime())[0]
        const dayDifference = Math.ceil((nearestExpiryDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24))

        if (dayDifference <= 0) {
            return 'Due today'
        }

        return `${dayDifference} ${dayDifference === 1 ? 'day' : 'days'} remaining`
    })

    function scrollToDocumentsModule() {
        nextTick(() => {
            util.scrollToElement('documents')
        })
    }

    function handleMissingDocumentsAlertClick() {
        scrollToDocumentsModule()
        setTimeout(() => {
            documentsModule.value?.openSignatureRequestDialog()
        }, 350)
    }

    function handlePendingDocumentsAlertClick() {
        scrollToDocumentsModule()
        setTimeout(() => {
            documentsModule.value?.focusFirstPendingBundle()
        }, 350)
    }
</script>

<template>
    <div
        v-if="show"
        class="my-6 space-y-4 mx-3 md:mx-0"
    >
        <button
            v-if="missingDocumentsAlerts.length > 0"
            type="button"
            class="w-full rounded-lg border border-dashed border-lifeworx-red-500/50 bg-lifeworx-red-500/10 px-3 md:px-6 py-3 text-lifeworx-red-500 hover:bg-lifeworx-red-500/15 transition-colors"
            @click="handleMissingDocumentsAlertClick()"
        >
            <div class="flex items-center gap-3 text-left sm:gap-4">
                <IconDocumentEdit16Regular class="size-5 flex-shrink-0" />
                <div class="min-w-0 flex-1 text-base flex items-center">
                    <span class="block font-bold text-base md:text-sm sm:inline sm:mr-5 flex-shrink-0"> {{ missingDocumentsAlerts.length }} Missing Documents</span>
                    <span class="block font-semibold text-xs sm:inline flex-wrap">{{ missingDocumentsAlertNames }}</span>
                </div>
                <div class="ml-auto flex items-center justify-end gap-3 font-bold whitespace-nowrap text-lg md:text-sm">
                    <span>Resolve</span>
                    <div class="flex size-5 items-center justify-center rounded-full bg-lifeworx-red-500 text-white">
                        <ArrowRightIcon class="size-3" />
                    </div>
                </div>
            </div>
        </button>

        <button
            v-else-if="pendingDocumentsCountAlert > 0"
            type="button"
            class="w-full rounded-lg border border-dashed border-lifeworx-orange-500/60 bg-lifeworx-orange-500/10 px-3 md:px-6 py-3 text-lifeworx-orange-500 hover:bg-lifeworx-orange-500/15 transition-colors"
            @click="handlePendingDocumentsAlertClick()"
        >
            <div class="flex items-center gap-3 text-left sm:gap-4">
                <IconDocumentError16Filled class="size-5 flex-shrink-0" />
                <div class="min-w-0 flex-1 text-base flex items-center">
                    <span class="block font-bold text-base md:text-sm sm:inline sm:mr-5 flex-shrink-0">{{ pendingDocumentsCountAlert }} {{ pendingDocumentsCountAlert === 1 ? 'Document' : 'Documents' }} <br class="md:hidden">Pending Completion</span>
                    <span v-if="pendingDocumentsRemainingText" class="block font-semibold text-xs sm:inline sm:truncate">{{ pendingDocumentsRemainingText }}</span>
                </div>
                <div class="ml-auto flex items-center justify-end gap-3 font-semibold whitespace-nowrap text-lg md:text-sm">
                    <span>View</span>
                    <div class="flex size-5 items-center justify-center rounded-full bg-lifeworx-orange-500 text-white">
                        <ArrowRightIcon class="size-3" />
                    </div>
                </div>
            </div>
        </button>
    </div>
</template>
